import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ImageIcon, Instagram, MapPin, Phone, Plus, Search, Trash2, Wallet } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CUSTOMER_SOURCES, fmtIDR } from "@/lib/constants";
import { NumInput } from "@/components/NumInput";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";

const CAN_MANAGE = ["owner", "admin_input"];
const EMPTY = { name: "", phone: "", instagram: "", source: "", sales_admin: "", notes: "", addresses: [] };

export default function Customers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState(null);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [creditCust, setCreditCust] = useState(null);
  const [creditData, setCreditData] = useState(null);
  const [creditForm, setCreditForm] = useState({ amount: "", direction: "in", reason: "" });
  const [creditBusy, setCreditBusy] = useState(false);

  const canManage = CAN_MANAGE.includes(user.role);
  const isOwner = user.role === "owner";

  const openCredit = async (c) => {
    setCreditCust(c);
    setCreditData(null);
    setCreditForm({ amount: "", direction: "in", reason: "" });
    try {
      const { data } = await api.get(`/customers/${c.id}/credit`);
      setCreditData(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addCreditEntry = async () => {
    setCreditBusy(true);
    try {
      await api.post(`/customers/${creditCust.id}/credit`, {
        amount: Number(creditForm.amount),
        direction: creditForm.direction,
        transaction_type: "OWNER_ADJUSTMENT",
        reason: creditForm.reason.trim(),
      });
      toast.success("Credit entry added");
      setCreditForm({ amount: "", direction: "in", reason: "" });
      const { data } = await api.get(`/customers/${creditCust.id}/credit`);
      setCreditData(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCreditBusy(false);
    }
  };

  const load = useCallback(async () => {
    if (!canManage) {
      setCustomers([]);
      return;
    }
    try {
      const params = search.trim() ? { q: search.trim() } : {};
      const { data } = await api.get("/customers", { params });
      setCustomers(data);
    } catch (e) {
      toast.error(apiError(e));
      setCustomers([]);
    }
  }, [search, canManage]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name,
      phone: c.phone || "",
      instagram: c.instagram || "",
      source: c.source || "",
      sales_admin: c.sales_admin || "",
      notes: c.notes || "",
      addresses: c.addresses || [],
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    const body = {
      name: form.name.trim(),
      phone: form.phone.trim() || null,
      instagram: form.instagram.trim() || null,
      source: form.source || null,
      sales_admin: form.sales_admin.trim() || null,
      notes: form.notes,
      addresses: form.addresses.filter((a) => a.address.trim()).map((a) => ({ label: a.label || null, address: a.address.trim() })),
    };
    try {
      if (editing) {
        await api.patch(`/customers/${editing.id}`, body);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", body);
        toast.success("Customer created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    return (
      <div data-testid="customers-forbidden" className="grid place-items-center rounded-md border border-dashed py-20">
        <p className="font-medium text-muted-foreground">Customer Master is only available to the Owner and Admin Input.</p>
      </div>
    );
  }

  return (
    <div data-testid="customers-page">
      <button
        data-testid="back-to-orders"
        onClick={() => navigate("/orders")}
        className="mb-3 flex h-10 items-center gap-1 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Customer Orders
      </button>
      <div className="mb-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-extrabold tracking-tight">Customer Master</h1>
            <p className="text-sm text-muted-foreground">Saved customer details — no retyping per order</p>
          </div>
          {canManage && (
            <Button data-testid="add-customer-button" className="h-12 shrink-0 px-4" onClick={openNew}>
              <Plus className="h-4 w-4" /> Add Customer
            </Button>
          )}
        </div>
        <div className="relative mt-3">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
          <Input
            data-testid="customer-master-search"
            className="h-12 pl-9"
            placeholder="Search name, phone, or Instagram…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {customers === null ? (
        <div className="grid place-items-center py-24">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : customers.length === 0 ? (
        <div data-testid="customers-empty" className="grid place-items-center rounded-md border border-dashed py-20 text-center">
          <p className="font-medium text-muted-foreground">No customers found</p>
        </div>
      ) : (
        <div data-testid="customers-list" className="space-y-2.5">
          {customers.map((c) => (
            <div key={c.id} className="flex items-stretch gap-2">
              <button
                data-testid={`customer-card-${c.id}`}
                onClick={() => canManage && openEdit(c)}
                className="min-w-0 flex-1 rounded-md border bg-card p-4 text-left transition-transform hover:-translate-y-[1px] active:scale-[0.99]"
              >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent font-heading text-sm font-bold text-accent-foreground">
                  {c.name.slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold">{c.name}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {[c.phone, c.instagram && `@${c.instagram}`, c.source].filter(Boolean).join(" · ") || "no contact info"}
                  </p>
                </div>
                {c.sales_admin && (
                  <span className="shrink-0 rounded-md border bg-[#EDE4DE] px-2 py-0.5 text-[10px] font-bold text-stone-600">Admin: {c.sales_admin}</span>
                )}
              </div>
              {(c.addresses?.length > 0 || c.notes) && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {c.addresses?.length > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-md border bg-[#EDE4DE] px-2 py-0.5 text-[10px] font-semibold text-stone-600">
                      <MapPin className="h-3 w-3" /> {c.addresses.length} address{c.addresses.length === 1 ? "" : "es"}
                    </span>
                  )}
                  {c.notes && <span className="truncate text-[10px] text-muted-foreground">{c.notes}</span>}
                </div>
              )}
              </button>
              <button
                data-testid={`customer-credit-${c.id}`}
                onClick={() => openCredit(c)}
                aria-label={`Credit ledger for ${c.name}`}
                className="grid w-14 shrink-0 place-items-center rounded-md border bg-card text-rose-500 transition-colors hover:bg-rose-50"
              >
                <Wallet className="h-5 w-5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <Drawer open={open} onOpenChange={setOpen} shouldScaleBackground={false}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">{editing ? "Edit Customer" : "Add Customer"}</DrawerTitle>
          </DrawerHeader>
          <div className="max-h-[75vh] space-y-4 overflow-y-auto px-4 pb-8" data-testid="customer-form">
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input data-testid="cust-name-input" className="h-12" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label>WhatsApp / Phone</Label>
                <Input data-testid="cust-phone-input" className="h-12" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Instagram</Label>
                <Input data-testid="cust-instagram-input" className="h-12" placeholder="username" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label>Source</Label>
                <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                  <SelectTrigger data-testid="cust-source-select" className="h-12">
                    <SelectValue placeholder="Channel" />
                  </SelectTrigger>
                  <SelectContent>
                    {CUSTOMER_SOURCES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`cust-source-${s.toLowerCase()}`}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Sales Admin</Label>
                <Input data-testid="cust-admin-input" className="h-12" value={form.sales_admin} onChange={(e) => setForm({ ...form, sales_admin: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea data-testid="cust-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Delivery addresses (optional, can be completed later)</Label>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="add-address-button"
                  className="h-9"
                  onClick={() => setForm({ ...form, addresses: [...form.addresses, { label: "", address: "" }] })}
                >
                  <Plus className="h-3.5 w-3.5" /> Address
                </Button>
              </div>
              {form.addresses.map((a, i) => (
                <div key={i} className="space-y-2 rounded-md border bg-accent/30 p-3" data-testid={`address-row-${i}`}>
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid={`address-label-${i}`}
                      className="h-10"
                      placeholder="Label (e.g. Home, Office)"
                      value={a.label || ""}
                      onChange={(e) => setForm({ ...form, addresses: form.addresses.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)) })}
                    />
                    <button
                      type="button"
                      data-testid={`address-remove-${i}`}
                      aria-label="Remove address"
                      className="shrink-0 text-red-500"
                      onClick={() => setForm({ ...form, addresses: form.addresses.filter((_, xi) => xi !== i) })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <Textarea
                    data-testid={`address-text-${i}`}
                    placeholder="Full address"
                    value={a.address}
                    onChange={(e) => setForm({ ...form, addresses: form.addresses.map((x, xi) => (xi === i ? { ...x, address: e.target.value } : x)) })}
                  />
                </div>
              ))}
            </div>
            <Button data-testid="save-customer-button" className="h-12 w-full text-base" onClick={save} disabled={saving || !form.name.trim()}>
              {saving ? "Saving…" : editing ? "Save Changes" : "Create Customer"}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      <Drawer open={!!creditCust} onOpenChange={(o) => !o && setCreditCust(null)} shouldScaleBackground={false}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">Credit — {creditCust?.name}</DrawerTitle>
          </DrawerHeader>
          <div className="max-h-[75vh] space-y-3 overflow-y-auto px-4 pb-8" data-testid="credit-drawer">
            {!creditData ? (
              <div className="grid place-items-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : (
              <>
                <div className="rounded-md border bg-rose-50 p-4 text-center" data-testid="credit-balance-box">
                  <p className="text-xs font-bold uppercase tracking-wide text-rose-500">Available Credit</p>
                  <p className="font-heading text-3xl font-extrabold text-rose-700" data-testid="credit-balance-value">{fmtIDR(creditData.balance)}</p>
                </div>
                {isOwner && (
                  <div className="space-y-2 rounded-md border bg-accent/30 p-3" data-testid="credit-add-form">
                    <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Owner adjustment</p>
                    <div className="flex gap-2">
                      <NumInput
                        data-testid="credit-amount"
                        mode="money"
                        className="h-11 flex-1 bg-white"
                        placeholder="Amount (IDR)"
                        value={creditForm.amount}
                        onValue={(v) => setCreditForm({ ...creditForm, amount: v })}
                      />
                      <Select value={creditForm.direction} onValueChange={(v) => setCreditForm({ ...creditForm, direction: v })}>
                        <SelectTrigger data-testid="credit-direction" className="h-11 w-32 bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="in" data-testid="credit-direction-in">+ Add</SelectItem>
                          <SelectItem value="out" data-testid="credit-direction-out">− Reduce</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      data-testid="credit-reason"
                      className="h-11 bg-white"
                      placeholder="Reason (required)"
                      value={creditForm.reason}
                      onChange={(e) => setCreditForm({ ...creditForm, reason: e.target.value })}
                    />
                    <Button
                      data-testid="credit-add-submit"
                      className="h-11 w-full"
                      onClick={addCreditEntry}
                      disabled={creditBusy || !(Number(creditForm.amount) > 0) || !creditForm.reason.trim()}
                    >
                      Add Entry
                    </Button>
                  </div>
                )}
                {creditData.entries.length === 0 ? (
                  <p className="py-6 text-center text-xs text-muted-foreground" data-testid="credit-no-entries">No credit entries yet</p>
                ) : (
                  <div className="space-y-1.5" data-testid="credit-entries">
                    {creditData.entries.map((e) => (
                      <div key={e.id} className="flex items-center justify-between rounded-md border bg-card p-2.5">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold">{e.transaction_type.replace(/_/g, " ")}</p>
                          <p className="truncate text-[11px] text-muted-foreground">
                            {e.reason} · {e.created_by_name}
                          </p>
                        </div>
                        <div className="shrink-0 text-right">
                          <p className={`text-sm font-bold ${e.direction === "in" ? "text-emerald-700" : "text-red-600"}`}>
                            {e.direction === "in" ? "+" : "−"}{fmtIDR(e.amount)}
                          </p>
                          {e.balance_after != null && <p className="text-[10px] text-muted-foreground">bal {fmtIDR(e.balance_after)}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
